import React, { useEffect, useState } from "react";
import { Button, Container, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage, getWordConnector } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import { useUser } from "../../../apis/user/UserProvider";
import { SENHA_PADRAO, UsuarioPostVM, UsuarioResponse } from "../../../services/userService";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { useRole } from '../../../apis/role/RoleProvider';
import WarehouseSection from "../../../components/WarehouseSection";
import User from "../../../types/user";
import warehouseClient from "../../../types/warehouseClient";

/**
 * Rótulo do usuário na lista de espelhamento. O nome vem primeiro porque a lista é
 * ordenada por ele. Inclui o username, que é único, para que a escolha feita no
 * datalist possa ser mapeada de volta sem ambiguidade.
 */
const rotuloDoUsuario = (usuario: User): string =>
    [usuario.fullName, usuario.username, usuario.email].filter(Boolean).join(" | ");

/** Nome usado para ordenar a lista, com o login como alternativa para quem não tem nome. */
const nomeParaOrdenar = (usuario: User): string => usuario.fullName || usuario.username || "";

/** Ordena por nome respeitando acentuação e maiúsculas/minúsculas do português. */
const ordenaPorNome = (usuarios: User[]): User[] =>
    [...usuarios].sort((a, b) =>
        nomeParaOrdenar(a).localeCompare(nomeParaOrdenar(b), "pt-BR", { sensitivity: "base" })
    );

const CreateUserView: React.FC = () => {
    const { t } = useTranslation();
    const { postUsuario, fetchUsuarios, fetchUsuarioById } = useUser();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { roles, fetchRole } = useRole();
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
    
    // Usuário novo já nasce habilitado e com a senha padrão preenchida. A senha padrão
    // é reconhecida no login, que exige a troca por uma senha própria no primeiro acesso.
    const [formData, setFormData] = useState({
        id: 0,
        fullName: "",
        username: "",
        email: "",
        password: SENHA_PADRAO,
        confirmPassword: SENHA_PADRAO,
        enabled: true,
        accountExpired: false,
        accountLocked: false,
        passwordExpired: false,
        roleId: "",
        selectedWarehouses: [] as number[]
    });

    // Espelhamento: copia o perfil de acesso (perfil + armazéns/clientes) de um usuário existente.
    const [usuariosDisponiveis, setUsuariosDisponiveis] = useState<User[]>([]);
    const [textoDoEspelho, setTextoDoEspelho] = useState("");
    const [usuarioEspelhado, setUsuarioEspelhado] = useState<UsuarioResponse | null>(null);
    const [carregandoEspelho, setCarregandoEspelho] = useState(false);
    const [erroEspelho, setErroEspelho] = useState<string | null>(null);
    const [warehousesDoEspelho, setWarehousesDoEspelho] = useState<warehouseClient[] | undefined>(undefined);

    useEffect(() => {
        const fetchData = async () => {
            try {
                await fetchRole();
            } catch (err) {
                console.error("Erro ao carregar roles", err);
            }
        };

        fetchData();
    }, []);

    // Sem filtro a API traz somente os usuários ativos, que são os que podem ser espelhados.
    useEffect(() => {
        const carregaUsuarios = async () => {
            try {
                setUsuariosDisponiveis(ordenaPorNome(await fetchUsuarios()));
            } catch (err) {
                console.error("Erro ao carregar usuários para espelhamento", err);
            }
        };

        carregaUsuarios();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const navItems: NavBarItem[] = [
        { label: t('user.labels.home'), route: "/", iconName: "house-door" },
        { label: t('user.labels.list'), route: "/User", iconName: "list" },
        { label: t('user.labels.new'), route: "/User/create", iconName: "plus-circle" },
        { label: t('user.labels.compare'), route: "/user/comparar", iconName: "columns-gap" },
    ];

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    /**
     * Copia para o formulário o perfil de acesso do usuário escolhido: o perfil (role)
     * e os armazéns/clientes. Dados pessoais (nome, login, e-mail, senha) e a situação
     * da conta continuam sendo preenchidos manualmente.
     */
    const aplicaEspelho = async (usuario: User) => {
        if (!usuario.id) return;

        setCarregandoEspelho(true);
        setErroEspelho(null);

        try {
            const resposta = await fetchUsuarioById(usuario.id);

            if (!resposta?.data) {
                throw new Error("Usuário não encontrado");
            }

            const origem = resposta.data;
            const roleId = origem.secUserSecRoles?.[0]?.secRole?.id;

            setFormData(prev => ({ ...prev, roleId: roleId ? String(roleId) : "" }));
            setWarehousesDoEspelho(origem.worksTo || []);
            setUsuarioEspelhado(origem);
        } catch (err) {
            console.error("Erro ao espelhar usuário:", err);
            setErroEspelho(getErrorMessage("mirrorUserError"));
            setUsuarioEspelhado(null);
            setWarehousesDoEspelho(undefined);
        } finally {
            setCarregandoEspelho(false);
        }
    };

    /** Desfaz o espelhamento sem mexer no que está digitado na busca. */
    const descartaEspelho = () => {
        setUsuarioEspelhado(null);
        setErroEspelho(null);
        // Array vazio (e não undefined) para o WarehouseSection reprocessar sem nada marcado.
        setWarehousesDoEspelho([]);
        setFormData(prev => ({ ...prev, roleId: "", selectedWarehouses: [] }));
    };

    const limpaEspelho = () => {
        setTextoDoEspelho("");
        descartaEspelho();
    };

    const handleEspelhoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const texto = e.target.value;
        setTextoDoEspelho(texto);

        // O datalist preenche o campo com o rótulo completo quando uma opção é escolhida.
        const escolhido = usuariosDisponiveis.find(u => rotuloDoUsuario(u) === texto);

        if (escolhido) {
            aplicaEspelho(escolhido);
        } else if (usuarioEspelhado) {
            // Mexeu no texto depois de espelhar: desfaz para não deixar o formulário
            // com permissões copiadas sem o aviso correspondente na tela.
            descartaEspelho();
        }
    };

    const validateForm = (): { isValid: boolean; errors: Record<string, string[]> } => {
        const errors: Record<string, string[]> = {};
        
        if (!formData.username.trim()) {
            errors.username = [getUiInterface("fieldRequiredSingle", { field: t("username") })];
        } else if (formData.username.length < 3 || formData.username.length > 50) {
            errors.username = [getUiInterface("minEMaxCaracters")];
        }
        
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = [getUiInterface("invalidEmail")];
        }

        if (!formData.email) {
            errors.email = [getUiInterface("fieldRequiredSingle", { field: t("email") })];
        }
        
        if (formData.fullName && formData.fullName.length > 100) {
            errors.fullName = [getUiInterface("maxCaracters")];
        }
        
        if (!formData.password) {
            errors.password = [getUiInterface("fieldRequiredSingle", { field: t("password") })];
        }
        if (!formData.confirmPassword) {
            errors.confirmPassword = [getUiInterface("fieldRequiredSingle", { field: t("confirmPassword") })];
        } else if (formData.password.length < 6 || formData.password.length > 100) {
            errors.password = [getUiInterface("passwordMinimumLength")];
        }
        
        if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = [getUiInterface("noEqualsPassword")];
        }
        
        if (!formData.roleId) {
            errors.roleId = [getUiInterface("selectRole")];
        }
        
        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(false);
        setErrorMessage(null);
        
        const { isValid, errors } = validateForm();
        
        if (!isValid) {
            setValidationErrors(errors);
            setError(true);
            setLoading(false);
            return;
        }

        try {
            const payload: UsuarioPostVM = {
                id: formData.id,
                fullName: formData.fullName,
                userName: formData.username,
                email: formData.email,
                password: formData.password,
                enabled: formData.enabled,
                accountExpired: formData.accountExpired,
                accountLocked: formData.accountLocked,
                passwordExpired: formData.passwordExpired,
                confirmPassword: formData.confirmPassword,
                roleIds: [Number(formData.roleId)],
                wareHouseClientIds: formData.selectedWarehouses
            };

            console.log('Enviando payload:', payload);
            const response = await postUsuario(payload);
            console.log('Resposta recebida:', response);
            
            if (response) {
                // Tenta extrair o ID de diferentes formatos de resposta
                const userId = response.data?.id || response.data?.id;
                console.log('UserID extraído:', userId);
                
                if (userId) {
                    // A API devolve o resultado do envio do e-mail de acesso junto da criação.
                    const mensagens = [
                        getSuccessMessage("createUser", { id: userId }),
                        ...(response.messages || []),
                    ];

                    navigate(`/User`, {
                        state: {
                            message: mensagens.join(" "),
                            variant: "success",
                        },
                    });
                    return;
                }
            }

            console.warn('Resposta sem ID válido:', response);
            throw new Error(getUiInterface("createUserError"));
        } catch (error: any) {
            console.error("Erro ao criar usuário:", error);
            setError(true);
            
            if (error.response?.data?.errors) {
                setValidationErrors(error.response.data.errors);
            } else if (error.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage(error.message || getUiInterface("createUserError"));
            }
        } finally {
            setLoading(false);
        }
    };

    const renderFieldErrors = (fieldName: string) => {
        if (!validationErrors[fieldName]) return null;
        
        return (
            <div className="text-danger mt-1">
                {validationErrors[fieldName].map((error, index) => (
                    <div key={index}>{error}</div>
                ))}
            </div>
        );
    };
    const customStyles = {
        errorBorder: {
            borderColor: '#dc3545',
            backgroundImage: 'none', 
            paddingRight: 'calc(1.5em + 0.75rem)', 
            '&:focus': {
                borderColor: '#dc3545',
                boxShadow: '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
            }
        }
    };
    return (
        <Container fluid>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getPageName("createUser")}</h2>
            
            {errorMessage && (
                <div className="alert alert-danger" role="alert">
                    {errorMessage}
                </div>
            )}
            
            <Form onSubmit={handleSubmit}>
                {/* Espelhamento: preenche perfil e armazéns a partir de um usuário existente. */}
                <div className="border rounded p-3 mb-4 bg-light">
                    <Form.Group>
                        <Form.Label className="fw-bold">
                            <i className="bi bi-people-fill me-2"></i>
                            {t('user.labels.mirrorUser')}
                        </Form.Label>
                        <div className="d-flex gap-2 align-items-start">
                            <div style={{ flex: 1, maxWidth: "600px" }}>
                                <Form.Control
                                    type="text"
                                    list="usuarios-para-espelhar"
                                    value={textoDoEspelho}
                                    onChange={handleEspelhoChange}
                                    placeholder={t('user.labels.mirrorPlaceholder')}
                                    autoComplete="off"
                                    disabled={carregandoEspelho}
                                />
                                <datalist id="usuarios-para-espelhar">
                                    {usuariosDisponiveis.map((u) => (
                                        <option key={u.id} value={rotuloDoUsuario(u)} />
                                    ))}
                                </datalist>
                            </div>
                            {usuarioEspelhado && (
                                <Button variant="outline-secondary" onClick={limpaEspelho}>
                                    {t('user.labels.mirrorClear')}
                                </Button>
                            )}
                        </div>

                        <Form.Text className="text-muted">
                            {t('user.labels.mirrorHelp')}
                        </Form.Text>

                        {carregandoEspelho && (
                            <div className="mt-2 text-muted">{t('user.labels.mirrorLoading')}</div>
                        )}

                        {erroEspelho && (
                            <div className="mt-2 text-danger">{erroEspelho}</div>
                        )}

                        {usuarioEspelhado && !carregandoEspelho && (
                            <div className="alert alert-info mt-2 mb-0 py-2">
                                {t('user.labels.mirrorApplied', {
                                    user: usuarioEspelhado.username || "",
                                    role: usuarioEspelhado.secUserSecRoles?.[0]?.secRole?.authority || "-",
                                    count: usuarioEspelhado.worksTo?.length || 0,
                                })}
                            </div>
                        )}
                    </Form.Group>
                </div>

                <div className="d-flex gap-4 mb-3">
                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.name')}</Form.Label>
                        <Form.Control
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                        />
                         {renderFieldErrors("fullName")}
                    </Form.Group>

                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.username')} *</Form.Label>
                        <Form.Control
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.username}
                            style={{
                                backgroundImage: 'none',
                            }}
                        />
                        {renderFieldErrors("username")}
                    </Form.Group>
                </div>

                <div className="d-flex gap-4 mb-3">
                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.password')} *</Form.Label>
                        <Form.Control
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.password}
                            style={{
                                backgroundImage: 'none',
                            }}
                        />
                        {/* O campo é mascarado: sem esta dica ninguém sabe qual senha está preenchida. */}
                        {formData.password === SENHA_PADRAO && (
                            <Form.Text className="text-muted">
                                {t('user.labels.defaultPasswordHint', { password: SENHA_PADRAO })}
                            </Form.Text>
                        )}
                        {renderFieldErrors("password")}
                    </Form.Group>

                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.confirmPassword')} *</Form.Label>
                        <Form.Control
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.confirmPassword}
                            style={{
                                backgroundImage: 'none',
                            }}
                        />
                        {renderFieldErrors("confirmPassword")}
                    </Form.Group>

                    <Form.Group style={{ flex: 1 }}>
                        <Form.Label>{t('user.fields.email')} *</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            isInvalid={!!validationErrors.email}
                            style={{
                                backgroundImage: 'none',
                            }}
                        />
                        {renderFieldErrors("email")}
                    </Form.Group>
                </div>

                <div className="d-flex gap-4 mb-3">
                    <Form.Group style={{ width: "30%" }}>
                        <Form.Label>{t('user.fields.role')} *</Form.Label>
                        <Form.Select
                            name="roleId"
                            value={formData.roleId}
                            onChange={handleSelectChange}
                            isInvalid={!!validationErrors.roleId}
                            style={validationErrors.roleId ? {
                                borderColor: '#dc3545',
                                backgroundImage: 'none'
                            } : undefined}
                        >
                            <option value="">{t('user.labels.selectRole')}</option>
                            {roles?.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.authority}
                                </option>
                            ))}
                        </Form.Select>
                         {renderFieldErrors("roleId")}
                    </Form.Group>

                    <Form.Group className="d-flex align-items-center gap-4">
                        <Form.Check
                            type="checkbox"
                            name="enabled"
                            label={t('user.fields.enabled')}
                            checked={formData.enabled}
                            onChange={handleInputChange}
                        />
                        <Form.Check
                            type="checkbox"
                            name="accountExpired"
                            label={t('user.fields.accountExpired')}
                            checked={formData.accountExpired}
                            onChange={handleInputChange}
                        />
                        <Form.Check
                            type="checkbox"
                            name="accountLocked"
                            label={t('user.fields.accountLocked')}
                            checked={formData.accountLocked}
                            onChange={handleInputChange}
                        />
                        <Form.Check
                            type="checkbox"
                            name="passwordExpired"
                            label={t('user.fields.passwordExpired')}
                            checked={formData.passwordExpired}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                </div>

                <WarehouseSection
                    selectedWarehouses={formData.selectedWarehouses}
                    setSelectedWarehouses={(warehouses) =>
                        setFormData(prev => ({ ...prev, selectedWarehouses: warehouses }))
                    }
                    isEditing={true}
                    userWarehouses={warehousesDoEspelho}
                />

                <div className="mt-3 mb-3 d-flex gap-2">
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? t('user.labels.saving') : getUiInterface("save")}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/User')}>
                        {getUiInterface("cancel")}
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default CreateUserView;